import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { useAppSelector } from '../../store/hooks';

export interface ColumnFormData {
  id?: string;
  tableId: string;
  columnName: string;
  dataType: string;
  description: string;
  businessRules: string;
  exampleValues: string;
  isNullable: boolean;
  isPrimaryKey: boolean;
  isForeignKey: boolean;
  foreignKeyReference: string;
  keywords: string[];
  isCore: boolean;
  alwaysInclude: boolean;
  allowedValues: string[];
}

interface CreateEditColumnModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: ColumnFormData) => void;
  mode: 'create' | 'edit';
  saving: boolean;
  saveError: string | null;
  tableId: string;
}

const CreateEditColumnModal: React.FC<CreateEditColumnModalProps> = ({
  open,
  onClose,
  onSave,
  mode,
  saving,
  saveError,
  tableId,
}) => {
  const { selectedColumn } = useAppSelector((state) => state.columns);

  const [formData, setFormData] = useState({
    columnName: '',
    dataType: '',
    description: '',
    businessRules: '',
    exampleValues: '',
    isNullable: false,
    isPrimaryKey: false,
    isForeignKey: false,
    foreignKeyReference: '',
    keywords: '',
    isCore: false,
    alwaysInclude: false,
    allowedValues: '',
  });

  useEffect(() => {
    if (mode === 'edit' && selectedColumn) {
      setFormData({
        columnName: selectedColumn.columnName || '',
        dataType: selectedColumn.dataType || '',
        description: selectedColumn.description || '',
        businessRules: selectedColumn.businessRules || '',
        exampleValues: selectedColumn.exampleValues || '',
        isNullable: selectedColumn.isNullable || false,
        isPrimaryKey: selectedColumn.isPrimaryKey || false,
        isForeignKey: selectedColumn.isForeignKey || false,
        foreignKeyReference: selectedColumn.foreignKeyReference || '',
        keywords: selectedColumn.keywords?.join(', ') || '',
        isCore: selectedColumn.isCore || false,
        alwaysInclude: selectedColumn.alwaysInclude || false,
        allowedValues: selectedColumn.allowedValues?.join(', ') || '',
      });
    } else if (mode === 'create') {
      setFormData({
        columnName: '',
        dataType: '',
        description: '',
        businessRules: '',
        exampleValues: '',
        isNullable: false,
        isPrimaryKey: false,
        isForeignKey: false,
        foreignKeyReference: '',
        keywords: '',
        isCore: false,
        alwaysInclude: false,
        allowedValues: '',
      });
    }
  }, [mode, selectedColumn, open]);

  const handleChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    const keywordsArray = formData.keywords
      .split(',')
      .map((k) => k.trim())
      .filter((k) => k.length > 0);

    const allowedValuesArray = formData.allowedValues
      .split(',')
      .map((v) => v.trim())
      .filter((v) => v.length > 0);

    const data: ColumnFormData = {
      tableId,
      columnName: formData.columnName,
      dataType: formData.dataType,
      description: formData.description,
      businessRules: formData.businessRules,
      exampleValues: formData.exampleValues,
      isNullable: formData.isNullable,
      isPrimaryKey: formData.isPrimaryKey,
      isForeignKey: formData.isForeignKey,
      foreignKeyReference: formData.foreignKeyReference,
      keywords: keywordsArray,
      isCore: formData.isCore,
      alwaysInclude: formData.alwaysInclude,
      allowedValues: allowedValuesArray,
    };

    if (mode === 'edit' && selectedColumn) {
      data.id = selectedColumn.id;
    }

    onSave(data);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {mode === 'create' ? 'Crear Nueva Columna' : 'Editar Columna'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {saveError && (
            <Alert severity="error">{saveError}</Alert>
          )}

          <TextField
            label="Nombre de la Columna"
            value={formData.columnName}
            onChange={(e) => handleChange('columnName', e.target.value)}
            fullWidth
            required
          />

          <TextField
            label="Tipo de Dato"
            value={formData.dataType}
            onChange={(e) => handleChange('dataType', e.target.value)}
            fullWidth
            required
          />

          <TextField
            label="Descripción"
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            fullWidth
            multiline
            rows={3}
          />

          <TextField
            label="Reglas de Negocio"
            value={formData.businessRules}
            onChange={(e) => handleChange('businessRules', e.target.value)}
            fullWidth
            multiline
            rows={3}
          />

          <TextField
            label="Valores de Ejemplo"
            value={formData.exampleValues}
            onChange={(e) => handleChange('exampleValues', e.target.value)}
            fullWidth
          />

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isNullable}
                  onChange={(e) => handleChange('isNullable', e.target.checked)}
                />
              }
              label="Nullable"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isPrimaryKey}
                  onChange={(e) => handleChange('isPrimaryKey', e.target.checked)}
                />
              }
              label="Llave Primaria"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isForeignKey}
                  onChange={(e) => handleChange('isForeignKey', e.target.checked)}
                />
              }
              label="Llave Foránea"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isCore}
                  onChange={(e) => handleChange('isCore', e.target.checked)}
                />
              }
              label="Es Core"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.alwaysInclude}
                  onChange={(e) => handleChange('alwaysInclude', e.target.checked)}
                />
              }
              label="Siempre Incluir"
            />
          </Box>

          {formData.isForeignKey && (
            <TextField
              label="Referencia de Llave Foránea"
              value={formData.foreignKeyReference}
              onChange={(e) => handleChange('foreignKeyReference', e.target.value)}
              fullWidth
            />
          )}

          <TextField
            label="Palabras Clave (separadas por coma)"
            value={formData.keywords}
            onChange={(e) => handleChange('keywords', e.target.value)}
            fullWidth
            helperText="Ingrese las palabras clave separadas por coma"
          />

          <TextField
            label="Valores Permitidos (separados por coma)"
            value={formData.allowedValues}
            onChange={(e) => handleChange('allowedValues', e.target.value)}
            fullWidth
            helperText="Ingrese los valores permitidos separados por coma"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" disabled={saving}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving || !formData.columnName || !formData.dataType}
          startIcon={saving ? <CircularProgress size={20} /> : null}
        >
          {saving ? 'Guardando...' : mode === 'create' ? 'Crear' : 'Actualizar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateEditColumnModal;
